<template lang="pug">
.review(:class="[layout]")
  CarouselModal(v-if="modal" :value="modalIndex" @close="modal = null")
    .modal-review-item(v-for="card in modal")
      img(:src="card.src" :alt="card.alt")
      .caption(v-if="card.caption") {{card.caption}}
  .author
    strong {{review.author.name}}
    div {{review.author.location}}
  .review-contents
    .rating
      Stars(:rating="review.rating")
      .date {{review.dateRelative}}
    .title {{ review.title}}
    .text(v-text="review.text")
    .icon-flex.recommend(v-if="review.recommended")
      Icon(name="check")
      span(v-html="__('<strong>Yes,</strong> I recommend this product')")
    .images(v-if="review.images.length > 0" )
      .thumb( v-for="(img, i) in review.images" )
        StaticImage(:src="img.src" :alt="reviewPhotoAlt(rogue, img.caption, review.author.name)" :height="300" :width="400" @click="showModal(review.images, i)" )
    .helpful(v-if="!review.syndicated" :class="myVote && 'voted'")
      span {{ __('Helpful?') }}
      .help-btn(@click="vote('helpful')" :class="{'active-helpful': myVote === 'helpful'}")
        | {{ __('Yes') }}
        | &middot;&nbsp;
        span {{review.helpful  + (newVote && myVote === 'helpful' ? 1 : 0)}}
      .help-btn(@click="vote('notHelpful')"  :class="{'active-unhelpful': myVote === 'notHelpful'}")
        | {{ __('No') }}
        | &middot;&nbsp;
        span {{review.notHelpful + (newVote && myVote === 'notHelpful' ? 1 : 0) }}
    .syndicated(v-if="originalAttributionName && originalAttributionLink")
      | {{ __('Originally posted on ') }}
      rogue-link(:productId="review.originalProduct && review.originalProduct.productId" :to="originalAttributionLink") {{ originalAttributionName }}
    .syndicated(v-else-if="originalAttributionName")
      | {{ __('Originally posted on the {name}', { name: originalAttributionName }) }}
    .syndicated(v-else-if="review.syndicated")
      | {{ __('Originally posted on {source}', {source: review.syndicated}) }}
    .client-response(v-for="response in review.responses")
      h4 {{ __('Response from Rogue Fitness:') }}
      .resp-date {{ response.dateRelative }}
      .text {{ response.text }}
      .images(v-if="response.images.length")
        .thumb(v-for="(img, i) in response.images")
          StaticImage(:src="img" :width="400" :height="300" alt="Response Image" @click="showModal(response.images, i)")
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useRogue } from '@/js/services/Rogue'
import { reviewPhotoAlt } from '@/js/services/reviews/photoAlt'
import type { Review, ReviewImg } from '@/js/services/reviews/reviewTypes'

const router = useRouter()
const route = useRoute()
const rogue = useRogue()

const props = withDefaults(defineProps<{
  review: Review,
  layout?: 'vertical' | 'horizontal'
}>(), {
  layout: 'horizontal',
})

const modal = ref<{ src: string, alt: string, caption?: string }[] | null>(null)
const modalIndex = ref<number>(0)
const myVote = ref<'helpful' | 'notHelpful' | null>(null)
const newVote = ref<boolean>(false)

const originalAttributionName = computed<string | null>(() => {
  // Prefer BV-provided OriginalProductName first. Name can display even without a link.
  const name = props.review.originalProductName || (props.review.originalProduct && props.review.originalProduct.name) || null
  
  return name || null
})

const originalAttributionLink = computed<string | null>(() => {
  const url = props.review?.originalProduct?.url || null
  if (!url) return null

  const resolve = router?.resolve || ((url: string) => {
    try { return { path: new URL(url, rogue?.baseURL || window.location.origin).pathname } } catch { return { path: url } }
  })

  const ssrPath = rogue?.ssrContext?.renderUrl?.split('?')[0] || ''
  const currentPath = route?.path || ssrPath || ''

  const linkPath = resolve(url)?.path || ''
  
  return linkPath !== currentPath ? url : null
})

onMounted(() => {
  try {
    const votes = JSON.parse(window.localStorage.getItem('bvrvws') || '{}')
    myVote.value = votes[props.review.id] || null
  } catch (e) {
    //
  }
})

function showModal(images: ReviewImg[] | string[], i: number) {
  // Response images are bare URLs; customer photos carry an optional caption
  modal.value = images.map(img => typeof img === 'string'
    ? { src: img, alt: 'Response Image' }
    : { src: img.src, alt: reviewPhotoAlt(rogue, img.caption, props.review.author.name), caption: img.caption })
  modalIndex.value = i
}

function vote(type: 'helpful' | 'notHelpful') {
  if (!myVote.value) {
    import(/* webpackChunkName: "reviews" */ '@/js/services/reviews/bazaarvoice').then(fns => fns.addFeedback(props.review.id, type, rogue, props.review))
    myVote.value = type
    newVote.value = true

    try {
      const votes = JSON.parse(window.localStorage.getItem('bvrvws') || '{}')
      votes[props.review.id] = type
      window.localStorage.setItem('bvrvws', JSON.stringify(votes))
    } catch (e) {
      //
    }
  }
}
</script>
<style lang="scss" scoped>

.review {
  font-size: $size-xs;
  margin: $space-m 0;
  border-bottom: 1px solid $color-gray;

  &.vertical {
    display: flex;
    flex-direction: column;
    gap: $space-xxs;
    margin: 0;

    .author {
      flex-basis: 0;
      flex-shrink: 0;
    }

    .review-contents {
      flex-grow: 1;
    }
  }

  @include not-mobile() {
    display: flex;

    .author {
      flex-basis: 230px;
      flex-shrink: 0;
    }
    .review-contents {
      flex-grow: 1;
    }
  }

  &:last-child {
    border-bottom: none;
  }

  .rating {
    display: flex;
    align-items: center;
    font-size: $size-l;

    .date {
      margin-left: $space-s;
      font-size: $size-xxs;
    }
  }

  .title {
    font-size: $size-s;
    font-weight: $weight-bold;
    margin: $space-xs 0 $space-m 0;
  }

  .text {
    margin: $space-s 0 $space-m 0;
    line-height: $line-height-l;
    white-space: pre-wrap;
  }

  .helpful {
    margin: $space-s 0;
    display: flex;
    align-items: center;
    font-size: $size-xxs;

    .help-btn {
      cursor: pointer;
      font-weight: $weight-bold;
      padding: $space-xxs;
      margin-left: $space-xs;
      display: flex;
      background-color: $color-gray-x-light;

      &:hover {
        background-color: $color-gray-light;
      }

      &.active-helpful {
        border: 1px solid $color-green-dark;
        color: $color-green-dark;
      }

      &.active-unhelpful {
        border: 1px solid $color-red-dark;
        color: $color-red-dark;
      }
    }

    &.voted .help-btn {
      pointer-events: none;
    }
  }

  .recommend {
    .icon {
      width: 14px;
      height: 14px;
    }
  }

  .images {
    display: flex;
    flex-wrap: wrap;
    margin: $space-s 0;

    .thumb {
      cursor: pointer;
      padding: 2px;

      img {
        border: 1px solid $color-gray-dark;
      }

      @include large-xl() {
        width: 180px
      }
      @include desktop() {
        width: 25%
      }
      @include not-desktop() {
        width: 50%;
      }
    }
  }

  .syndicated {
    background-color: $color-gray-x-light;
    margin: $space-s 0;
    padding: $space-xxs;

    a {
      color: $color-black-light !important;
    }
  }
}

.modal-review-item {
  padding: $space-l;
  pointer-events: all;
  background-color: $color-white;
  max-width: 85%;
  max-height: 100%;
  overflow-y: auto;

  img {
    display: block;
    margin: 0 auto $space-s auto;
    max-width: 100%;
    max-height: 70vh;
  }
}

.client-response {
  background-color: $color-gray-light;
  padding: $space-s;
  margin-bottom: $space-s;

  .text {
    margin-bottom: 0;
  }
}

</style>